import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { MdDelete } from 'react-icons/md';
import constants from '../../constants';

// GH-707: vacation ranges of an expert, same list (and same endpoints' data) the expert sees in the
// experts app. A vacation set with the old single-vacation dialog shows here with id 'legacy'.
type VacationRow = { _id: string; start: string; end: string; legacy?: boolean };
// API errors come as a string or {he,en}
const errMsg = (e: any) => {
	const m = e?.response?.data?.message;
	return typeof m === 'object' ? m?.he || m?.en : m;
};

const ExpertVacations = ({ instructorId }: { instructorId: string }) => {
	const notify = useNotify();
	const [rows, setRows] = useState<VacationRow[]>([]);
	const [start, setStart] = useState('');
	const [end, setEnd] = useState('');
	const [busy, setBusy] = useState(false);
	const base = `${constants.API_URL}instructor/${instructorId}/vacations`;

	const load = useCallback(async () => {
		try {
			const { data } = await axios.get<VacationRow[]>(base);
			setRows(data);
		} catch {}
	}, [base]);
	useEffect(() => {
		if (instructorId) load();
	}, [instructorId, load]);

	const add = async () => {
		if (!start || !end) return notify('חובה להגדיר תאריך התחלה וסיום', { type: 'warning' });
		if (start > end) return notify('סיום החופשה חייב להיות לאחר תחילת החופשה', { type: 'warning' });
		setBusy(true);
		try {
			const { data } = await axios.post<VacationRow[]>(base, { start, end });
			setRows(data);
			setStart('');
			setEnd('');
			notify('החופשה נוספה', { type: 'success' });
		} catch (e: any) {
			notify(errMsg(e) || 'שמירת החופשה נכשלה', { type: 'error' });
		} finally {
			setBusy(false);
		}
	};
	const remove = async (id: string) => {
		try {
			const { data } = await axios.delete<VacationRow[]>(`${base}/${id}`);
			setRows(data);
			notify('החופשה נמחקה', { type: 'success' });
		} catch (e: any) {
			notify(errMsg(e) || 'מחיקת החופשה נכשלה', { type: 'error' });
		}
	};
	const fmt = (d: string) => moment(d).format('DD/MM/YYYY');

	return (
		<Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, my: 1 }} dir="rtl">
			<Typography variant="subtitle1" fontWeight={600} gutterBottom>
				חופשות
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={1}>
				בטווחים האלה לא ניתן לקבוע פגישות. המומחה רואה ועורך את אותה רשימה באפליקציית המומחים.
			</Typography>
			{rows.length === 0 ? (
				<Typography variant="body2" color="text.secondary" mb={1}>
					אין חופשות מוגדרות
				</Typography>
			) : (
				<Stack gap={0.5} mb={1}>
					{rows.map((row) => (
						<Stack key={row._id} direction="row" alignItems="center" gap={1}>
							<Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
								{fmt(row.start)} – {fmt(row.end)}
							</Typography>
							<IconButton size="small" color="error" title="מחיקה" onClick={() => remove(row._id)}>
								<MdDelete />
							</IconButton>
						</Stack>
					))}
				</Stack>
			)}
			<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
				<TextField
					type="date"
					size="small"
					label="התחלה"
					InputLabelProps={{ shrink: true }}
					value={start}
					onChange={(e) => setStart(e.target.value)}
				/>
				<TextField
					type="date"
					size="small"
					label="סיום"
					InputLabelProps={{ shrink: true }}
					value={end}
					onChange={(e) => setEnd(e.target.value)}
				/>
				<Button variant="outlined" size="small" disabled={busy} onClick={add}>
					הוספת חופשה
				</Button>
			</Stack>
		</Box>
	);
};

export default ExpertVacations;
